import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { HubPageGrid } from "@/components/content/HubPageGrid";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const templatePages = [
  {
    title: "Lawn Care Homepage Template",
    description: "Copy-paste homepage structure for lawn care websites. Headlines, sections, and CTAs that convert.",
    href: "/website-templates/lawn-care-homepage-template/"
  },
  {
    title: "Landscaping Homepage Template",
    description: "A proven homepage template for landscaping companies. Layout, copy, and design guidance.",
    href: "/website-templates/landscaping-homepage-template/"
  },
  {
    title: "Lawn Care Service Page Template",
    description: "Create service pages that rank and convert. Template for mowing, fertilization, and more.",
    href: "/website-templates/lawn-care-service-page-template/"
  },
  {
    title: "Landscaping Service Page Template",
    description: "Template for landscaping service pages: hardscaping, design, installation, and maintenance.",
    href: "/website-templates/landscaping-service-page-template/"
  },
  {
    title: "Service Area Page Template",
    description: "Local SEO template for city and neighborhood pages. Rank in more service areas.",
    href: "/website-templates/service-area-page-template/"
  },
  {
    title: "Lawn Care Website SEO Checklist",
    description: "Complete SEO checklist for lawn care websites. On-page, technical, and local SEO essentials.",
    href: "/website-templates/lawn-care-website-seo-checklist/"
  },
  {
    title: "Landscaping Website SEO Checklist",
    description: "SEO checklist for landscaping websites. Everything you need to rank higher on Google.",
    href: "/website-templates/landscaping-website-seo-checklist/"
  },
  {
    title: "Lawn Care Quote Form Template",
    description: "High-converting quote form template. What fields to include and how to structure your form.",
    href: "/website-templates/lawn-care-quote-form-template/"
  }
];

const faqs = [
  {
    question: "How do I use these templates for my lawn care website?",
    answer: "Each template includes copy-paste sections, recommended headlines, and structure guidance. Use them as a starting point and customize with your business details, service area, and unique selling points."
  },
  {
    question: "Are these templates free to use?",
    answer: "Yes! All templates and checklists are completely free. Use them on your own website or share them with your web developer."
  },
  {
    question: "Will these templates help my website rank on Google?",
    answer: "Yes, when implemented correctly. Our templates follow SEO best practices for lawn care and landscaping businesses. Combine with our SEO checklists for best results."
  },
  {
    question: "What's the difference between homepage and service page templates?",
    answer: "Homepage templates focus on your overall business, trust signals, and main services. Service page templates go deep on a single service to rank for specific keywords like 'lawn mowing near me.'"
  },
  {
    question: "How do service area page templates work?",
    answer: "Service area templates help you create pages for each city or neighborhood you serve. This improves local SEO and helps you rank for '[service] in [city]' searches."
  },
  {
    question: "What should be on my lawn care quote form?",
    answer: "Keep it simple: name, phone, email, address, service needed, and urgency. Our template shows you the exact fields and copy that get the most submissions."
  },
  {
    question: "Can I use these templates on Wix, WordPress, or Squarespace?",
    answer: "Yes! These templates work on any website platform. They provide structure and copy that you can implement in any builder or CMS."
  },
  {
    question: "How often should I update my website content?",
    answer: "Review your main pages quarterly. Update service pages when you add new services or change pricing. Keep your service areas current as your business expands."
  },
  {
    question: "Do you offer done-for-you website services?",
    answer: "Yes! If you'd rather have professionals handle your website, check out our website subscription service. We build and maintain your site for $200-$300/month."
  },
  {
    question: "What's included in the SEO checklists?",
    answer: "Our SEO checklists cover on-page SEO (titles, meta descriptions, headers, content), technical SEO (speed, mobile, structure), and local SEO (GBP, citations, reviews)."
  }
];

const WebsiteTemplatesHub = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/";
  
  return (
    <>
      <SEO
        title="Lawn Care Website Templates & Checklists | Leaf Rush"
        description="Free lawn care website templates and SEO checklists. Copy-paste homepage, service page, and quote form templates that convert visitors into leads."
        canonical={pageUrl}
        ogUrl={pageUrl}
        keywords="lawn care website templates, landscaping website template, lawn care SEO checklist"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Website Templates"
        pageDescription="Free website templates and SEO checklists for lawn care and landscaping businesses. Homepage, service page, and quote form templates."
        faqs={faqs}
      />

      <TopCTA />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Templates & Checklists</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care Website{" "}
              <span className="text-primary">Templates & Checklists</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Free, copy-paste templates for your lawn care or landscaping website. 
              Homepage layouts, service page structures, and SEO checklists that work.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section className="py-12 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">Want Us to Build Your Website?</h2>
            <p className="text-center text-muted-foreground mb-8">
              These templates are great for DIY. But if you want a done-for-you website, 
              check out our subscription service:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/websites/">
                <Button size="lg" className="gap-2">
                  Website Pricing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </NavLink>
              <NavLink to="/lawn-care-website-design/">
                <Button size="lg" variant="outline">
                  Lawn Care Websites
                </Button>
              </NavLink>
              <NavLink to="/landscaping-website-design/">
                <Button size="lg" variant="outline">
                  Landscaping Websites
                </Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Grid of Template Pages */}
      <section className="py-16">
        <div className="container px-4 md:px-6">
          <h2 className="text-3xl font-bold mb-8 text-center">All Templates & Checklists</h2>
          <HubPageGrid items={templatePages} />
        </div>
      </section>

      <MidPageCTA />

      {/* Internal Links */}
      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Resources"
              links={[
                { to: "/comparisons", label: "Website Builder Comparisons" },
                { to: "/website-fixes", label: "Website Problems & Fixes" },
                { to: "/service-pages-that-rank", label: "Service Pages That Rank" },
                { to: "/service-area-pages-local-seo", label: "Service Area Pages for Local SEO" },
                { to: "/growth-add-ons/google-ads", label: "Google Ads Management" },
                { to: "/growth-add-ons/gbp", label: "Google Business Profile Optimization" },
                { to: "/resources", label: "All Resources" },
                { to: "/contact", label: "Contact Us" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <BottomCTA />

      <CalendlyEmbed />
    </>
  );
};

export default WebsiteTemplatesHub;
